/* eslint-disable import/no-extraneous-dependencies */
import { useTranslation } from 'react-i18next';
import { TbBulb, TbChevronDown } from 'react-icons/tb';
import { Link } from 'react-router';

import { APPS } from '@cccom/config/app-config';
import { App } from '@cccom/config/app-type';
import { useEnvironment } from '@cccom/config/env';
import { Button } from '@cccom/ui/src/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cccom/ui/src/components/shadcn/dropdown-menu';

export type AppLogoProps = {
  logoSrc: string;
  homeUrl: string;
  appName?: string;
  alt?: string;
};

export type AppSwitcherProps = {
  appList: App[];
  appLogo: AppLogoProps;
};

export function AppSwitcher({ appList, appLogo }: AppSwitcherProps) {
  const env = useEnvironment();
  const [t] = useTranslation();

  const activeApps = appList
    .map((app) => APPS[app?.id])
    .filter((app) => app?.status !== 'FUTURE');

  const { logoSrc, homeUrl, alt, appName } = appLogo;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label="Switch Apps"
          data-cy="switchApp"
          className="flex items-center gap-2"
        >
          <img src={logoSrc} alt={alt} className="h-6" />
          {appName && (
            <span className="text-base text-blue-950">{appName}</span>
          )}
          <TbChevronDown />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="ms-6 mt-1 px-2 text-sm text-gray-600">
        <DropdownMenuGroup>
          {activeApps.map((app) => (
            <DropdownMenuItem
              key={app.id}
              asChild
              className="relative cursor-pointer"
            >
              <Link
                reloadDocument={app.name !== appName}
                to={app.name === appName ? homeUrl : APPS.getUrl(env, app)}
                className="mb-1 flex items-center gap-4 px-4 py-2 no-underline hover:no-underline"
                data-cy={`switchTo${app.name}`}
              >
                <img src={app.logo} alt="logo" className="h-6 w-6" />
                <span>{app.name}</span>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild className="cursor-pointer">
          <Link
            to={APPS.getUrl(env, APPS.HOME)}
            className="flex items-center gap-4 px-4 py-2 no-underline hover:no-underline [&_svg]:size-6"
          >
            <TbBulb />
            <span>{t('home.menu.more_solutions')}</span>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
